// Pure helpers that predict the server's auto-rebalance + midpoint
// math exactly. Keeping the math in sync means the optimistic cache
// write produces the same position values as the eventual mutation
// response — so Apollo's response merge is a no-op and the UI doesn't
// "settle" a second later.

import type { ApolloCache, Reference } from '@apollo/client';
import type { BoardCoreFragment } from '../../../generated/graphql';
import type { CardMoveArgs, ColumnMoveArgs } from './useBoardDragHandlers';

const POSITION_BUCKET = 1024;
const POSITION_EPSILON = 1e-9;

export function rewireCardColumnsInCache(
  cache: ApolloCache<unknown>,
  args: { cardId: string; toColumnId: string; columnIds: string[] }
): void {
  const { cardId, toColumnId, columnIds } = args;
  const cardCacheId = cache.identify({
    __typename: 'BoardCard',
    _id: cardId,
  });
  if (!cardCacheId) return;

  for (const columnId of columnIds) {
    const columnCacheId = cache.identify({
      __typename: 'BoardColumn',
      _id: columnId,
    });
    if (!columnCacheId) continue;

    cache.modify({
      id: columnCacheId,
      fields: {
        cards(existing, { readField, toReference }) {
          const refs = (
            Array.isArray(existing) ? existing : []
          ) as readonly Reference[];

          if (columnId !== toColumnId) {
            if (!refs.some((ref) => readField('_id', ref) === cardId)) {
              return existing;
            }
            return refs.filter((ref) => readField('_id', ref) !== cardId);
          }

          if (refs.some((ref) => readField('_id', ref) === cardId)) {
            return refs;
          }
          const cardRef = toReference(cardCacheId);
          return cardRef ? [...refs, cardRef] : refs;
        },
      },
    });
  }
}

function midpoint(prev: number | undefined, next: number | undefined): number {
  if (prev === undefined && next === undefined) return POSITION_BUCKET;
  if (prev === undefined && next !== undefined) return next - POSITION_BUCKET;
  if (prev !== undefined && next === undefined) return prev + POSITION_BUCKET;
  return (prev! + next!) / 2;
}

function needsRebalance<T extends { position: number }>(items: T[]): boolean {
  if (items.length < 2) return false;
  const sorted = items.slice().sort((a, b) => a.position - b.position);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].position - sorted[i - 1].position <= POSITION_EPSILON) {
      return true;
    }
  }
  return false;
}

function restripeInPlace<T extends { _id: string; position: number }>(
  items: T[]
): void {
  const sorted = items.slice().sort((a, b) => a.position - b.position);
  sorted.forEach((entry, i) => {
    const target = items.find((x) => x._id === entry._id);
    if (target) target.position = (i + 1) * POSITION_BUCKET;
  });
}

export function moveCardInBoardCache(
  board: BoardCoreFragment,
  args: CardMoveArgs
): BoardCoreFragment {
  const cloned = structuredClone(board);

  // Locate the moving card and its source/target columns.
  let movingCard:
    | BoardCoreFragment['columns'][number]['cards'][number]
    | undefined;
  let sourceCol: BoardCoreFragment['columns'][number] | undefined;
  for (const col of cloned.columns) {
    const found = col.cards.find((c) => c._id === args.cardId);
    if (found) {
      movingCard = found;
      sourceCol = col;
      break;
    }
  }
  if (!movingCard || !sourceCol) return board;

  const targetCol = cloned.columns.find((c) => c._id === args.toColumnId);
  if (!targetCol) return board;

  // Pre-flight: if target column is dense, restripe it (mirrors server).
  if (needsRebalance(targetCol.cards)) {
    restripeInPlace(targetCol.cards);
  }

  // Cross-column move: remove the card from the source column array
  // so it doesn't appear in two columns simultaneously.
  if (sourceCol._id !== targetCol._id) {
    sourceCol.cards = sourceCol.cards.filter((c) => c._id !== args.cardId);
  }

  // "Others" = target column's cards excluding the moving card,
  // sorted ascending for prev/next lookup.
  const others = targetCol.cards
    .filter((c) => c._id !== args.cardId)
    .sort((a, b) => a.position - b.position);

  let prev: number | undefined;
  let next: number | undefined;
  if (args.afterCardId) {
    const idx = others.findIndex((c) => c._id === args.afterCardId);
    if (idx < 0) return board;
    prev = others[idx].position;
    next = others[idx + 1]?.position;
  } else {
    next = others[0]?.position;
  }

  movingCard.position = midpoint(prev, next);

  // For cross-column moves, append to target column (its array order
  // doesn't matter — sort-by-position resolves the visual order).
  if (sourceCol._id !== targetCol._id) {
    targetCol.cards.push(movingCard);
  }

  return cloned;
}

export function moveColumnInBoardCache(
  board: BoardCoreFragment,
  args: ColumnMoveArgs
): BoardCoreFragment {
  const cloned = structuredClone(board);

  // Pre-flight: if the columns row is dense, restripe (mirrors server).
  if (needsRebalance(cloned.columns)) {
    restripeInPlace(cloned.columns);
  }

  const moving = cloned.columns.find((c) => c._id === args.columnId);
  if (!moving) return board;

  const others = cloned.columns
    .filter((c) => c._id !== args.columnId)
    .sort((a, b) => a.position - b.position);

  let prev: number | undefined;
  let next: number | undefined;
  if (args.afterColumnId) {
    const idx = others.findIndex((c) => c._id === args.afterColumnId);
    if (idx < 0) return board;
    prev = others[idx].position;
    next = others[idx + 1]?.position;
  } else {
    next = others[0]?.position;
  }

  moving.position = midpoint(prev, next);

  return cloned;
}

// Predicts the `position` value `moveCardInBoardCache` would write to
// the moving card — the number Apollo needs in `optimisticResponse` so
// the UI doesn't "settle" when the server response merges in. Returns
// undefined when the card or target column isn't found (mirrors
// `moveCardInBoardCache`'s no-op fallback).
export function predictCardPosition(
  board: BoardCoreFragment,
  args: CardMoveArgs
): number | undefined {
  const next = moveCardInBoardCache(board, args);
  if (next === board) return undefined;
  for (const col of next.columns) {
    const card = col.cards.find((c) => c._id === args.cardId);
    if (card) return card.position;
  }
  return undefined;
}

// Predicts the `position` value `moveColumnInBoardCache` would write to
// the moving column. Returns undefined when the column isn't found.
export function predictColumnPosition(
  board: BoardCoreFragment,
  args: ColumnMoveArgs
): number | undefined {
  const next = moveColumnInBoardCache(board, args);
  if (next === board) return undefined;
  const col = next.columns.find((c) => c._id === args.columnId);
  return col?.position;
}

/**
 * Writes the positions `moveCardInBoardCache` computes for EVERY card, not
 * just the moving one — the card-side twin of `writeColumnPositionsInCache`.
 *
 * Without this, a restripe can never reach the cache. Repeated moves halve the
 * gaps between cards (`midpoint` is `(prev + next) / 2`) until a column's
 * positions cluster within POSITION_EPSILON. From then on every move restripes
 * the clone to 1024/2048/3072… and picks a midpoint in that roomy space, but
 * only the moving card's position is written back — so it sorts above the
 * still-clustered remainder and pins to the end of the column wherever it is
 * dropped, and the cluster never breaks up. That is the "stuck card".
 *
 * Unchanged positions are returned as-is so Apollo does not mark them dirty.
 */
export function writeCardPositionsInCache(
  cache: ApolloCache<unknown>,
  board: BoardCoreFragment,
  args: CardMoveArgs
): void {
  const next = moveCardInBoardCache(board, args);
  if (next === board) return;

  for (const column of next.columns) {
    for (const card of column.cards) {
      const cardCacheId = cache.identify({
        __typename: 'BoardCard',
        _id: card._id,
      });
      if (!cardCacheId) continue;

      cache.modify({
        id: cardCacheId,
        fields: {
          position: (existing: number) =>
            existing === card.position ? existing : card.position,
        },
      });
    }
  }
}

/**
 * Writes the positions `moveColumnInBoardCache` computes for EVERY column,
 * not just the moving one.
 *
 * A mutation's `optimisticResponse` can only carry the moving column, because
 * that is all `boardColumnMove` returns. But `moveColumnInBoardCache` restripes
 * the whole row whenever two positions drift within POSITION_EPSILON — so on a
 * restripe the moving column would land in restriped coordinates while every
 * other column kept its old value, and the optimistic order would be computed
 * across two different coordinate spaces. Writing them all keeps the row
 * internally consistent until the server response confirms it.
 */
export function writeColumnPositionsInCache(
  cache: ApolloCache<unknown>,
  board: BoardCoreFragment,
  args: ColumnMoveArgs
): void {
  const next = moveColumnInBoardCache(board, args);
  if (next === board) return;

  for (const column of next.columns) {
    const columnCacheId = cache.identify({
      __typename: 'BoardColumn',
      _id: column._id,
    });
    if (!columnCacheId) continue;

    cache.modify({
      id: columnCacheId,
      fields: { position: () => column.position },
    });
  }
}
